import React, { useState, useEffect } from 'react'
import { 
  RefreshCw,
  AlertCircle,
  Download,
  Table as TableIcon
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'

import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'

import { Label } from '@/components/ui/label'
import { UsageAnalyticsItem, UsageInterval, SortDirection, GroupBy } from '@/types'
import { partnerService } from '@/services/partnerService'
import { cn, formatPeriod, getChartColor } from '@/utils'
import { getDateRange } from '@/utils/date'
import { UsageChart } from './components/UsageChart'
import { ChartData, Filters } from './types'
import { ApiUsageHeroStats } from '@/components/molecules'

interface ApiUsageAnalyticsProps {
  className?: string
}

const ApiUsageAnalytics: React.FC<ApiUsageAnalyticsProps> = ({ className }) => {
  const [usageData, setUsageData] = useState<UsageAnalyticsItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<Filters>({
    interval: 'MONTH',
    sortDirection: 'DESC',
    groupBy: undefined
  })
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [refreshInterval, setRefreshInterval] = useState(30)
  const [timeRange, setTimeRange] = useState('7d')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [apiKeys, setApiKeys] = useState<{ key_id: string; key_name: string }[]>([])
  const [selectedApiKeyIds, setSelectedApiKeyIds] = useState<string[]>([])

  // Auto-refresh effect
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(loadUsageData, refreshInterval * 1000)
    return () => clearInterval(interval)
  }, [autoRefresh, refreshInterval])

  // Load data effect
  useEffect(() => {
    loadUsageData()
  }, [filters, timeRange, selectedApiKeyIds])

  // Load API keys effect
  useEffect(() => {
    loadApiKeys()
  }, [])

  const loadApiKeys = async () => {
    try {
      const response = await partnerService.listApiKeys()
      setApiKeys(response.data.map(key => ({ key_id: key.key_id, key_name: key.key_name })))
    } catch (err) {
      console.error('Error loading API keys:', err)
      setApiKeys([])
    }
  }

  const loadUsageData = async () => {
    setLoading(true)
    setError(null)
    try {
      const dateRange = getDateRange(timeRange)
      const response = await partnerService.getUsageAnalytics({
        interval: filters.interval,
        sort_direction: filters.sortDirection,
        group_by: filters.groupBy,
        start_date: dateRange.startDate,
        end_date: dateRange.endDate,
        api_key_ids: selectedApiKeyIds.length > 0 ? selectedApiKeyIds.join(',') : undefined
      })
      setUsageData(response.data || [])
      setLastUpdated(new Date())
    } catch (err) {
      if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You may not have permission to access Partner API features.')
      } else {
        setError('Failed to load usage analytics')
      }
      setUsageData([])
    } finally {
      setLoading(false)
    }
  }

  const handleExport = () => {
    const csvContent = `data:text/csv;charset=utf-8,Period,Calls${filters.groupBy === 'ENDPOINT' ? ',Endpoint' : ''}${filters.groupBy === 'STATUS_CODE' ? ',Status Code' : ''}\n${usageData.map(item => `${formatPeriod(item.period, filters.interval)},${item.call_count}${filters.groupBy === 'ENDPOINT' ? `,${item.endpoint || ''}` : ''}${filters.groupBy === 'STATUS_CODE' ? `,${item.status_code || ''}` : ''}`).join('\n')}`
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', `api-usage-${new Date().toISOString().split('T')[0]}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Utility functions
  const getTotalCalls = () => usageData.reduce((sum, item) => sum + item.call_count, 0)
  const getMaxCalls = () => {
    if (usageData.length === 0) return 0
    const maxFromData = Math.max(...usageData.map(item => item.call_count))
    return filters.interval === 'MONTH' ? Math.max(maxFromData, 100) : maxFromData // Ensure some height for empty months
  }
  const getAverageCalls = () => {
    if (usageData.length === 0) return 0
    const totalCalls = getTotalCalls()
    const monthsWithData = usageData.length
    return Math.round(totalCalls / monthsWithData)
  }

  // Generate all 12 months data
  const generateFullYearData = (): ChartData[] => {
    const currentYear = new Date().getFullYear()
    const months = []
    
    for (let month = 0; month < 12; month++) {
      const date = new Date(currentYear, month, 1)
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      
      // Find if we have data for this month
      const monthData = usageData.find(item => {
        const itemDate = new Date(item.period)
        return itemDate.getFullYear() === currentYear && itemDate.getMonth() === month
      })
      
      const groupValue = filters.groupBy === 'ENDPOINT' ? monthData?.endpoint : 
                        filters.groupBy === 'STATUS_CODE' ? monthData?.status_code : undefined
      
      months.push({
        period: monthKey,
        calls: monthData?.call_count || 0,
        endpoint: monthData?.endpoint,
        status_code: monthData?.status_code,
        groupValue,
        color: monthData ? getChartColor(month, groupValue) : '#e5e7eb', // gray for empty months
        rawPeriod: monthData?.period || date.toISOString()
      })
    }
    
    return months
  }

  // Prepare chart data - use full year data for monthly view
  const chartData: ChartData[] = filters.interval === 'MONTH' 
    ? generateFullYearData()
    : usageData.map((item, index) => {
        const groupValue = filters.groupBy === 'ENDPOINT' ? item.endpoint : 
                          filters.groupBy === 'STATUS_CODE' ? item.status_code : undefined
        
        return {
          period: formatPeriod(item.period, filters.interval),
          calls: item.call_count,
          endpoint: item.endpoint,
          status_code: item.status_code,
          groupValue,
          color: getChartColor(index, groupValue),
          rawPeriod: item.period
        }
      })

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header Section */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold font-schrifted bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                API Usage Analytics
              </CardTitle>
              <p className="text-muted-foreground mt-1">
                Monitor your API usage and performance with real-time insights
              </p>
              {lastUpdated && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge variant="secondary" className="text-xs">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </Badge>
                  {autoRefresh && (
                    <Badge variant="outline" className="text-xs">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-1" />
                      Auto-refresh every {refreshInterval}s
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="auto-refresh"
                  checked={autoRefresh}
                  onCheckedChange={(checked) => setAutoRefresh(checked as boolean)}
                />
                <Label htmlFor="auto-refresh" className="text-sm">Auto-refresh</Label>
                {autoRefresh && (
                  <Select value={refreshInterval.toString()} onValueChange={(value) => setRefreshInterval(Number(value))}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15s</SelectItem>
                      <SelectItem value="30">30s</SelectItem>
                      <SelectItem value="60">1m</SelectItem>
                      <SelectItem value="300">5m</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={loadUsageData}
                disabled={loading}
                className="flex items-center gap-2"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Stats Cards */}
      <ApiUsageHeroStats
        totalCalls={getTotalCalls()}
        peakCalls={getMaxCalls()}
        dataPoints={usageData.length}
        averageCalls={getAverageCalls()}
        loading={loading}
      />



      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={loadUsageData}>
                Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-bold font-schrifted">API Usage Over Time</h3>
              <p className="text-sm text-muted-foreground">
                {filters.interval.toLowerCase()} intervals • {usageData.length} data points • {timeRange === '7d' ? 'Last 7 days' : timeRange === '30d' ? 'Last 30 days' : timeRange === '90d' ? 'Last 90 days' : 'Last year'}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="flex items-center gap-2"
              >
                <Download size={16} />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {}}
                className="flex items-center gap-2"
              >
                <TableIcon size={16} />
                <span className="hidden sm:inline">Table</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {/* Filters Section */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold mb-4 text-muted-foreground">Data Filters & Controls</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>Time Range</Label>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="1y">Last year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Time Interval</Label>
                <Select 
                  value={filters.interval} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, interval: value as UsageInterval }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MINUTE">Minute</SelectItem>
                    <SelectItem value="HOUR">Hour</SelectItem>
                    <SelectItem value="DAY">Day</SelectItem>
                    <SelectItem value="WEEK">Week</SelectItem>
                    <SelectItem value="MONTH">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Sort Order</Label>
                <Select 
                  value={filters.sortDirection} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, sortDirection: value as SortDirection }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASC">Ascending (Oldest First)</SelectItem>
                    <SelectItem value="DESC">Descending (Newest First)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Group Data By</Label>
                <Select 
                  value={filters.groupBy || 'none'} 
                  onValueChange={(value) => setFilters(prev => ({ 
                    ...prev, 
                    groupBy: value === 'none' ? undefined : value as GroupBy 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No Grouping" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Grouping</SelectItem>
                    <SelectItem value="ENDPOINT">By API Endpoint</SelectItem>
                    <SelectItem value="STATUS_CODE">By Response Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Filter by API Keys</Label>
                <Select 
                  value={selectedApiKeyIds.length > 0 ? selectedApiKeyIds[0] : 'all'} 
                  onValueChange={(value) => {
                    if (value === 'all') {
                      setSelectedApiKeyIds([])
                    } else {
                      setSelectedApiKeyIds([value])
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All API Keys" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All API Keys</SelectItem>
                    {apiKeys.map(key => (
                      <SelectItem key={key.key_id} value={key.key_id}>
                        {key.key_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Chart */}
          <UsageChart
            data={chartData}
            loading={loading}
            groupBy={filters.groupBy}
          />
        </CardContent>
      </Card>
      

    </div>
  )
}

export default ApiUsageAnalytics 